// src/pages/OrganizationPage.js
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import useAuth from '../hooks/useAuth';
import {
    Box, Button, TextField, Typography, Container, Paper, Alert,
    CircularProgress, createTheme, ThemeProvider, CssBaseline, Stack,
    Snackbar // <<<< CORREÇÃO: Snackbar adicionado à importação
} from '@mui/material';
import { Edit } from '@mui/icons-material';
import myLogo from '../assets/sua-logo.png';

const darkTheme = createTheme({ palette: { mode: 'dark', primary: { main: '#ff834f' } } });
const lightTheme = createTheme({ palette: { primary: { main: '#ff834f' } } });

// ============================================================================
// VIEW PARA O ADMIN CRIAR SUA ORGANIZAÇÃO (ONBOARDING)
// ============================================================================
const SetupView = () => {
    const [companyName, setCompanyName] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            const response = await api.post('/organization/setup', { companyName });
            const { token } = response.data;
            localStorage.setItem('token', token);
            window.location.href = '/inicial';
        } catch (err) {
            setError(err.response?.data?.message || 'Não foi possível criar a organização.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <ThemeProvider theme={darkTheme}>
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" bgcolor="background.default">
                <CssBaseline />
                <Container component={Paper} elevation={12} maxWidth="xs" sx={{ p: 4, background: 'rgba(25, 35, 55, 0.6)', backdropFilter: 'blur(10.0px)', borderRadius: '10px' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <img src={myLogo} alt="Logo" style={{ width: '150px', marginBottom: '20px' }} />
                        <Typography component="h1" variant="h5" sx={{ mb: 1 }}>Bem-vindo!</Typography>
                        <Typography color="text.secondary" textAlign="center" sx={{ mb: 3 }}>Para finalizar, informe o nome da sua organização.</Typography>
                        <Box component="form" onSubmit={handleSubmit} noValidate>
                            <TextField margin="normal" required fullWidth id="companyName" label="Nome da sua Organização" name="companyName" autoFocus value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
                            {error && <Alert severity="error" sx={{ mt: 2, width: '100%' }}>{error}</Alert>}
                            <Button type="submit" fullWidth variant="contained" disabled={loading} sx={{ mt: 3, mb: 2, py: 1.5 }}>
                                {loading ? <CircularProgress size={24} color="inherit" /> : 'Criar Organização e Acessar'}
                            </Button>
                        </Box>
                    </Box>
                </Container>
            </Box>
        </ThemeProvider>
    );
};

// ============================================================================
// VIEW PARA O ADMIN GERENCIAR SUA ORGANIZAÇÃO JÁ EXISTENTE
// ============================================================================
const ManagementView = () => {
    const [organization, setOrganization] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [companyName, setCompanyName] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '' });

    const fetchOrganization = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get('/organization');
            if(response.data) {
                setOrganization(response.data);
                setCompanyName(response.data.name);
            } else {
                setError('Não foi possível encontrar os dados da sua organização.');
            }
        } catch (err) {
            setError('Erro ao carregar os dados da organização.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchOrganization();
    }, [fetchOrganization]);
    
    const handleUpdate = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.put('/organization', { companyName });
            setSnackbar({ open: true, message: 'Organização atualizada com sucesso!' });
            fetchOrganization();
            setIsEditing(false);
        } catch (err) {
            console.error("Erro ao atualizar organização", err);
            setError('Não foi possível salvar as alterações.');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box>;
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    if (!organization) {
        return <Alert severity="warning">Nenhuma organização encontrada.</Alert>;
    }
    
    return (
        <ThemeProvider theme={lightTheme}>
            <Container maxWidth="md">
                <Typography variant="h4" fontWeight={700} sx={{ mb: 4 }}>Minha Organização</Typography>
                <Paper sx={{ p: { xs: 2, sm: 4 }, borderRadius: 4 }}>
                    <Box component="form" onSubmit={handleUpdate}>
                        <Stack spacing={3}>
                            <TextField
                                label="Nome da Organização"
                                value={companyName}
                                onChange={(e) => setCompanyName(e.target.value)}
                                fullWidth
                                required
                                InputProps={{ readOnly: !isEditing }}
                                variant={isEditing ? "outlined" : "standard"}
                            />
                            <Stack direction="row" spacing={2} justifyContent="flex-end">
                                {isEditing ? (
                                    <>
                                        <Button onClick={() => { setIsEditing(false); setCompanyName(organization.name); }}>Cancelar</Button>
                                        <Button type="submit" variant="contained" disabled={loading}>
                                            {loading ? <CircularProgress size={24} /> : 'Salvar Alterações'}
                                        </Button>
                                    </>
                                ) : (
                                    <Button variant="contained" startIcon={<Edit />} onClick={() => setIsEditing(true)}>
                                        Editar
                                    </Button>
                                )}
                            </Stack>
                        </Stack>
                    </Box>
                </Paper>
            </Container>
            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} message={snackbar.message} />
        </ThemeProvider>
    );
}

// ============================================================================
// PÁGINA PRINCIPAL QUE DECIDE QUAL VISÃO MOSTRAR
// ============================================================================
const OrganizationPage = () => {
    const { user } = useAuth(); 

    if (!user) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box>;
    }

    if (user.role === 'admin' && user.companyId === null) {
        return <SetupView />;
    }
    
    if (user.role === 'admin' && user.companyId) {
        return <ManagementView />;
    }

    return (
        <Container>
            <Typography variant="h5">Página de Organização</Typography>
            <Typography color="text.secondary">Esta página é designada para administradores gerenciarem sua própria organização.</Typography>
        </Container>
    );
};

export default OrganizationPage;